import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Play, Download, RefreshCw } from "lucide-react";

const DEFAULT_SCRIPT = `Welcome to BizMatch! I'm your AI guide, and I'm excited to show you how our platform makes selling your business simple and effective.

Here's how BizMatch works in four easy steps:

Step 1: List Your Business. Create a detailed listing with photos, financials, and business details. You only pay $98 per month for private sellers or $89 per month for brokers with no upfront fees - making it risk-free to get started.

Step 2: We Find Buyers. Our advanced AI technology matches your business with qualified buyers from our database of over 4,000 pre-screened purchasers. No more waiting around hoping someone will find your listing.

Step 3: Connect and Close. You'll receive qualified leads instantly, and you only pay $15-$19 per lead when buyers request information about your business. This ensures you're only paying for genuine interest.

Step 4: Track Your Progress. Monitor your listing performance, lead quality, and conversion rates through your comprehensive analytics dashboard. Stay informed every step of the way.

With BizMatch, you get transparency, efficiency, and results. Ready to get started? Click the button below to list your first business today!`;

export function VideoManager() {
  const [script, setScript] = useState(DEFAULT_SCRIPT);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [videoFileName, setVideoFileName] = useState<string | null>(null);
  const { toast } = useToast();

  const checkExistingVideo = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('ai-avatar-videos')
        .list('', { limit: 1, sortBy: { column: 'created_at', order: 'desc' } });
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const { data: urlData } = supabase.storage
          .from('ai-avatar-videos')
          .getPublicUrl(data[0].name);
        
        setCurrentVideoUrl(urlData.publicUrl);
        setVideoFileName(data[0].name);
        return urlData.publicUrl;
      }
      
      return null;
    } catch (error) {
      console.error('Error checking existing video:', error);
      return null;
    }
  };

  const generateVideo = async (forceRegenerate = false) => {
    setIsGenerating(true);
    
    try {
      // First check if video exists unless forcing regeneration
      if (!forceRegenerate) {
        const existingUrl = await checkExistingVideo();
        if (existingUrl) {
          toast({
            title: "Video Already Exists",
            description: "Using existing video. Click 'Regenerate' to create a new one.",
          });
          setIsGenerating(false);
          return;
        }
      }

      const { data, error } = await supabase.functions.invoke('admin-generate-video', {
        body: { 
          text: script,
          force_regenerate: forceRegenerate
        }
      });

      if (error) throw error;

      if (data?.video_url) {
        setCurrentVideoUrl(data.video_url);
        setVideoFileName(data.file_name);
        
        toast({
          title: "Success",
          description: data.message || "Video generated successfully",
        });
      } else {
        throw new Error('No video URL returned');
      }
    } catch (error) {
      console.error('Error generating video:', error);
      let description = 'Failed to generate video';
      try {
        const ctx = (error as any)?.context;
        if (ctx?.body) {
          const parsed = JSON.parse(ctx.body);
          description = parsed?.error || parsed?.message || description;
        } else if ((error as any)?.message) {
          description = (error as any).message;
        }
        if (ctx?.status) description += ` (HTTP ${ctx.status})`;
      } catch (e) {
        // ignore parse errors
      }
      toast({
        title: "Error",
        description,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const previewVideo = () => {
    if (currentVideoUrl) {
      window.open(currentVideoUrl, '_blank');
    }
  };

  const downloadVideo = () => {
    if (currentVideoUrl) {
      const link = document.createElement('a');
      link.href = currentVideoUrl;
      link.download = videoFileName || 'ai-avatar-video.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Check for existing video on component mount
  useState(() => {
    checkExistingVideo();
  });

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>AI Avatar Video Manager</CardTitle>
        <CardDescription>
          Generate and manage the AI avatar presentation video for the homepage
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Script Editor */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Video Script</label>
          <Textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder="Enter the script for the AI avatar..."
            className="min-h-[200px]"
          />
          <p className="text-xs text-muted-foreground">
            Maximum 2000 characters. Current: {script.length}/2000
          </p>
        </div>

        {/* Current Video Status */}
        {currentVideoUrl && (
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">Current Video</h4>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-muted-foreground">
                File: {videoFileName || 'Unknown'}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={previewVideo}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                Preview
              </Button>
              <Button
                onClick={downloadVideo}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={() => generateVideo(false)}
            disabled={isGenerating || !script.trim()}
            className="flex items-center gap-2"
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {currentVideoUrl ? 'Check for Video' : 'Generate Video'}
          </Button>

          {currentVideoUrl && (
            <Button
              onClick={() => generateVideo(true)}
              disabled={isGenerating || !script.trim()}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Regenerate
            </Button>
          )}
        </div>

        {/* Instructions */}
        <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
          <h4 className="font-medium text-blue-900 mb-2">Instructions</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Edit the script above to customize the AI avatar presentation</li>
            <li>• Click "Generate Video" to create a new video (only if none exists)</li>
            <li>• Use "Regenerate" to force creation of a new video with updated script</li>
            <li>• The video will be automatically displayed on the homepage</li>
            <li>• Video generation typically takes 1-2 minutes</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}